// Global promise to signal when auth is ready
let authReadyResolver;
export const authReadyPromise = new Promise((resolve) => {
  authReadyResolver = resolve;
});
// Complete password reset using token
async function completePasswordReset({ email, token, newPassword }) {
  await api.post("/auth/reset-password", {
    email,
    token,
    new_password: newPassword,
  });
}
// Request password reset (email link)
async function resetPassword(email) {
  await api.post("/auth/request-password-reset", { email });
}
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api, { registerUnauthorizedHandler } from "@utils/axiosInstance";
import { clearRedirectAfterLogin } from "@auth/redirectAfterLogin";
// LWI-856 — dynamic import inside this helper keeps RUM out of the
// main bundle (preserves the LWI-699 lazy-load).
import {
  rumSetUser,
  rumClearUser,
  rumSetImpersonating,
} from "@utils/datadogRum";

const AuthContext = createContext(null);

const STORAGE_KEYS = {
  token: "token",
  tenant: "tenant", // string identifier (back-compat)
  tenantInfo: "tenant_info", // full tenant object {id,name,identifier}
  user: "user",
  roles: "roles",
  permissions: "permissions",
};

// LWI-582: a marker stored alongside the impersonation session in
// sessionStorage. Lets hydrate detect "this tab is mid-emulation" from a
// single key. The frontend uses this object via useAuth().impersonation
// to render the banner with admin + target identities.
const IMPERSONATION_KEY = "impersonation";

function safeReadJSON(storage, key, fallback) {
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function readStored() {
  return {
    token: localStorage.getItem(STORAGE_KEYS.token),
    tenant: localStorage.getItem(STORAGE_KEYS.tenant), // identifier (string)
    tenantInfo: safeReadJSON(localStorage, STORAGE_KEYS.tenantInfo, null), // object or null
    user: safeReadJSON(localStorage, STORAGE_KEYS.user, null),
    roles: safeReadJSON(localStorage, STORAGE_KEYS.roles, []),
    permissions: safeReadJSON(localStorage, STORAGE_KEYS.permissions, []),
  };
}

// LWI-582: read impersonation session from sessionStorage. Atomic check —
// if the marker exists, ALL of {token, user, roles, permissions, marker}
// must parse and be present. Any missing/unparsable → return null and the
// caller treats it as corruption (clears sessionStorage, falls back to
// localStorage admin hydrate).
function readImpersonationStored() {
  const marker = safeReadJSON(sessionStorage, IMPERSONATION_KEY, null);
  if (!marker) return null;
  const token = sessionStorage.getItem(STORAGE_KEYS.token);
  const user = safeReadJSON(sessionStorage, STORAGE_KEYS.user, null);
  const roles = safeReadJSON(sessionStorage, STORAGE_KEYS.roles, null);
  const permissions = safeReadJSON(
    sessionStorage,
    STORAGE_KEYS.permissions,
    null
  );
  if (!token || !user || !Array.isArray(roles) || !Array.isArray(permissions)) {
    return null;
  }
  return { token, user, roles, permissions, impersonation: marker };
}

function clearImpersonationStorage() {
  sessionStorage.removeItem(STORAGE_KEYS.token);
  sessionStorage.removeItem(STORAGE_KEYS.user);
  sessionStorage.removeItem(STORAGE_KEYS.roles);
  sessionStorage.removeItem(STORAGE_KEYS.permissions);
  sessionStorage.removeItem(IMPERSONATION_KEY);
}

// LWI-1748 — order-insensitive equality for the roles / permissions string
// lists, so a /me refresh that returns the same set keeps the old array.
function sameMembers(prev, next) {
  if (!Array.isArray(prev) || prev.length !== next.length) return false;
  const a = [...prev].sort();
  const b = [...next].sort();
  return a.every((v, i) => v === b[i]);
}

// LWI-1748 — minimum gap between focus / visibility-triggered /me refreshes.
const FOCUS_REFRESH_MIN_GAP_MS = 30 * 1000;

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [tenant, setTenant] = useState(null); // identifier string (back-compat)
  const [tenantInfo, setTenantInfo] = useState(null); // full tenant object
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  // LWI-661: surfaced to the StalePermissionsBanner. Set true when an
  // /me re-fetch returns a permission set that doesn't match what's
  // currently in memory. User-driven refresh clears it.
  const [permissionsStale, setPermissionsStale] = useState(false);

  // LWI-582: "admin" = normal session, "impersonation" = admin emulating.
  // mode controls which storage the persist effects write to (sessionStorage
  // for impersonation, localStorage for admin) so the admin's session never
  // overwrites itself when persist effects fire during emulation.
  const [mode, setMode] = useState("admin");
  const [impersonation, setImpersonation] = useState(null);

  // The unauthorized handler is registered once on mount and would close
  // over a stale `mode`. modeRef gives the closure access to the current
  // value at fire time. Same pattern for the impersonation marker if the
  // handler ever needs to read it.
  const modeRef = useRef("admin");
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  // hydrate once
  useEffect(() => {
    const adminBaseline = readStored();
    const impStored = readImpersonationStored();

    if (impStored) {
      // SessionStorage has impersonation state. The admin baseline must
      // still be present in localStorage so we can restore it on Exit;
      // without it, force-logout (cleared session in another tab? wiped
      // localStorage externally?). Otherwise we'd be stuck mid-emulation
      // with no way back.
      if (!adminBaseline.token || !adminBaseline.user) {
        clearImpersonationStorage();
        // LWI-592 — corrupt-state force-logout: drop any pending deep-link
        // stash so the user doesn't get teleported to a half-loaded page
        // when they sign back in. They can re-open the link if they meant
        // it.
        clearRedirectAfterLogin();
        // Force a full reset to /login. window.location.* fully resets
        // React state which is what we want here.
        window.location.href = "/login";
        return;
      }

      // tenant/tenantInfo always live in localStorage and don't change
      // during emulation — read them from the admin baseline.
      setMode("impersonation");
      setToken(impStored.token);
      setTenant(adminBaseline.tenant || null);
      setTenantInfo(adminBaseline.tenantInfo || null);
      setUser(impStored.user);
      setRoles(impStored.roles);
      setPermissions(impStored.permissions);
      setImpersonation(impStored.impersonation);
      // LWI-856 — RUM reports the admin (not the target) so audit
      // trails follow who actually clicked. Stash the target as a
      // custom `usr.impersonating` property.
      rumSetUser(adminBaseline.user);
      rumSetImpersonating(impStored.user);
      setLoading(false);
      if (authReadyResolver) authReadyResolver();
      return;
    }

    // Detected impersonation marker but it was corrupted (atomic check
    // failed); clear sessionStorage so we don't keep the broken state.
    if (sessionStorage.getItem(IMPERSONATION_KEY)) {
      clearImpersonationStorage();
    }

    // Normal admin hydrate.
    setMode("admin");
    setToken(adminBaseline.token || null);
    setTenant(adminBaseline.tenant || null);
    setTenantInfo(adminBaseline.tenantInfo || null);
    setUser(adminBaseline.user || null);
    setRoles(Array.isArray(adminBaseline.roles) ? adminBaseline.roles : []);
    setPermissions(
      Array.isArray(adminBaseline.permissions) ? adminBaseline.permissions : []
    );
    // LWI-856 — attach identity to the RUM session as soon as we know
    // who they are (this is the most common path: page reload while
    // already logged in).
    rumSetUser(adminBaseline.user);
    rumSetImpersonating(null);
    setLoading(false);
    if (authReadyResolver) authReadyResolver();
  }, []);

  // Reconcile the cached user/roles/permissions against the backend on every
  // token transition (hydrate + login). localStorage is frozen between
  // sessions, so admin-side edits to a user's supplier_id, roles, or
  // permissions would otherwise stay invisible to an active session until
  // logout — see LWI-572 (supplier_id) and LWI-661 (permissions).
  //
  // Implemented as a stable callback so the focus / visibility / interval
  // effects below can re-use it. `silent=true` suppresses the staleness
  // banner during initial hydration (the banner is for *changes* the user
  // didn't initiate; the first /me on hydrate is just bringing local state
  // up to date).
  const refreshAuth = useCallback(
    async ({ silent = false } = {}) => {
      try {
        const res = await api.get("/me");
        const fresh = res?.data;
        if (!fresh) return;
        const {
          roles: freshRoles,
          permissions: freshPermissions,
          ...freshUser
        } = fresh;
        // LWI-1748 — keep the previous references when /me returns what we
        // already have, so the memoized context value (and every useAuth
        // consumer + persist effect) isn't invalidated on each refresh.
        // Compare the MERGED user: the login payload carries keys /me
        // doesn't, and nested fields (supplier, tenants) must still update.
        setUser((prev) => {
          const merged = { ...(prev || {}), ...freshUser };
          return JSON.stringify(merged) === JSON.stringify(prev)
            ? prev
            : merged;
        });
        // LWI-856 — keep RUM identity in sync with /me when admin's
        // own name/email changes. Skip in impersonation mode because
        // /me returns the *target's* identity, and we want RUM to
        // remain the admin throughout the impersonation session.
        if (modeRef.current !== "impersonation") {
          rumSetUser(freshUser);
        }
        if (Array.isArray(freshRoles)) {
          setRoles((prev) =>
            sameMembers(prev, freshRoles) ? prev : freshRoles
          );
        }
        if (Array.isArray(freshPermissions)) {
          setPermissions((prev) => {
            const changed = !sameMembers(
              Array.isArray(prev) ? prev : [],
              freshPermissions
            );
            if (changed && !silent) setPermissionsStale(true);
            return changed ? freshPermissions : prev;
          });
        }
      } catch {
        // 401s are handled globally by the unauthorized handler below;
        // transient network/5xx errors leave the cached state in place.
      }
    },
    []
  );

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    let inFlight = null;
    let lastStartedAt = 0;
    const run = (opts) => {
      lastStartedAt = Date.now();
      inFlight = refreshAuth(opts).finally(() => {
        inFlight = null;
      });
      return inFlight;
    };
    run({ silent: true }).catch(() => {});

    // LWI-661 — refresh on focus / visibility / 10-min interval so admin
    // permission changes propagate without logout. Cap on staleness:
    // 10 min worst case when the tab stays focused continuously, less if
    // focus or visibility events fire. The cross-request RBAC cache TTL
    // on the backend is also 10 min, so staleness windows align.
    //
    // LWI-1748 — a tab return fires both focus and visibilitychange; one
    // handler skips while a refresh is in flight or one started within
    // FOCUS_REFRESH_MIN_GAP_MS. The interval stays unthrottled.
    const onReturn = () => {
      if (cancelled || document.visibilityState !== "visible") return;
      if (inFlight || Date.now() - lastStartedAt < FOCUS_REFRESH_MIN_GAP_MS) {
        return;
      }
      run();
    };
    const interval = setInterval(() => {
      if (!cancelled) run();
    }, 10 * 60 * 1000);

    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
      clearInterval(interval);
    };
  }, [token, refreshAuth]);

  // Force-clear auth state when any API call returns 401 (expired / revoked token).
  // We clear state directly rather than calling logout() to avoid a second API call.
  // AuthGuard detects isAuthenticated === false and redirects to /login automatically.
  //
  // LWI-582 scoped behavior: a 401 during impersonation means the impersonation
  // token died (TTL hit, target promoted to admin, target left tenant, server
  // revoked from another path). We clean up sessionStorage and restore the
  // admin's baseline from localStorage in a single React 18 batch. We do NOT
  // wipe localStorage in this branch — admin's session is still live.
  useEffect(() => {
    registerUnauthorizedHandler(() => {
      if (modeRef.current === "impersonation") {
        // Restore admin baseline from localStorage.
        const baseline = readStored();
        clearImpersonationStorage();
        setMode("admin");
        setImpersonation(null);
        setToken(baseline.token || null);
        setTenant(baseline.tenant || null);
        setTenantInfo(baseline.tenantInfo || null);
        setUser(baseline.user || null);
        setRoles(Array.isArray(baseline.roles) ? baseline.roles : []);
        setPermissions(
          Array.isArray(baseline.permissions) ? baseline.permissions : []
        );
        // LWI-856 — restore admin identity in RUM and drop the
        // impersonating marker. Admin's session is still live.
        rumSetUser(baseline.user);
        rumSetImpersonating(null);
        return;
      }

      // Admin-mode 401 → existing behavior: clear all state + localStorage.
      setToken(null);
      setTenant(null);
      setTenantInfo(null);
      setUser(null);
      setRoles([]);
      setPermissions([]);
      Object.values(STORAGE_KEYS).forEach((key) =>
        localStorage.removeItem(key)
      );
      // LWI-856 — session is gone; drop RUM identity too.
      rumClearUser();
      rumSetImpersonating(null);
    });
  }, []);

  // keep axios Authorization header in sync with context token (works on hydrate + updates)
  useEffect(() => {
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
  }, [token]);

  // LWI-582 persist routing: token, user, roles, permissions go to
  // sessionStorage when mode === "impersonation", localStorage otherwise.
  // tenant + tenantInfo always go to localStorage — they don't change
  // during emulation, and the axios interceptor reads tenant from
  // localStorage unconditionally.
  //
  // LWI-1717 — every persist effect waits for hydrate (`loading` false). On
  // the first commit the state is still the initial nulls / "admin" mode, and
  // persisting it would remove token/tenant from localStorage (signalling a
  // sign-out to every other tab) and, when reloading an impersonation tab,
  // wipe the admin's localStorage user and roles/permissions. Hydrate sets
  // the real values and loading=false in one batch, so the first persist
  // pass writes what was just read.
  useEffect(() => {
    if (loading) return;
    const storage = mode === "impersonation" ? sessionStorage : localStorage;
    if (token) storage.setItem(STORAGE_KEYS.token, token);
    else storage.removeItem(STORAGE_KEYS.token);
  }, [loading, token, mode]);

  // keep legacy identifier for back-compat (always localStorage)
  useEffect(() => {
    if (loading) return;
    if (tenant) localStorage.setItem(STORAGE_KEYS.tenant, tenant);
    else localStorage.removeItem(STORAGE_KEYS.tenant);
  }, [loading, tenant]);

  // full tenant object for name, etc (always localStorage)
  useEffect(() => {
    if (loading) return;
    if (tenantInfo)
      localStorage.setItem(STORAGE_KEYS.tenantInfo, JSON.stringify(tenantInfo));
    else localStorage.removeItem(STORAGE_KEYS.tenantInfo);
  }, [loading, tenantInfo]);

  useEffect(() => {
    if (loading) return;
    const storage = mode === "impersonation" ? sessionStorage : localStorage;
    if (user) storage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
    else storage.removeItem(STORAGE_KEYS.user);
  }, [loading, user, mode]);

  useEffect(() => {
    if (loading) return;
    const storage = mode === "impersonation" ? sessionStorage : localStorage;
    storage.setItem(STORAGE_KEYS.roles, JSON.stringify(roles || []));
  }, [loading, roles, mode]);

  useEffect(() => {
    if (loading) return;
    const storage = mode === "impersonation" ? sessionStorage : localStorage;
    storage.setItem(
      STORAGE_KEYS.permissions,
      JSON.stringify(permissions || [])
    );
  }, [loading, permissions, mode]);

  // Persist the impersonation marker to sessionStorage when set; clear when null.
  useEffect(() => {
    if (loading) return;
    if (impersonation) {
      sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(impersonation));
    } else {
      sessionStorage.removeItem(IMPERSONATION_KEY);
    }
  }, [loading, impersonation]);

  /**
   * Accepts:
   *  - tenant as an OBJECT { id, name, identifier }  ✅ preferred
   *  - tenant as a STRING identifier (back-compat)   ✅ supported
   */
  function setSession(session) {
    const {
      token: tkn,
      tenant: tn, // may be object OR string OR number
      user: usr,
      roles: rls = [],
      permissions: perms = [],
      // optional top-level fallbacks if your API ever returns these:
      tenant_identifier: tIdent,
      tenant_name: tName,
      tenant_id: tId,
    } = session || {};

    // LWI-582: setSession is called by login flows. Reset mode defensively
    // so a fresh login can never leak into sessionStorage if mode somehow
    // got stuck on "impersonation".
    setMode("admin");
    setImpersonation(null);

    setToken(tkn || null);

    let tenantIdentifier = null;
    let tenantObj = null;

    // 1) Preferred: full object
    if (tn && typeof tn === "object") {
      tenantIdentifier =
        tn.identifier ?? (typeof tn.id !== "undefined" ? String(tn.id) : null);
      tenantObj = tn;
    } else {
      // 2) String / number input
      if (typeof tn === "string") tenantIdentifier = tn;
      if (typeof tn === "number") tenantIdentifier = String(tn);
    }

    // 3) Try explicit fallbacks if API put them at the root
    if (!tenantObj && (tIdent || tName || tId)) {
      tenantIdentifier =
        tenantIdentifier || tIdent || (tId != null ? String(tId) : null);
      tenantObj = {
        id: tId ?? null,
        identifier: tIdent ?? tenantIdentifier ?? null,
        name: tName ?? null,
      };
    }

    // 4) If we STILL don't have a tenant object, try reuse what's already stored
    if (!tenantObj) {
      const stored = safeReadJSON(STORAGE_KEYS.tenantInfo, null);
      if (stored) {
        const match =
          (tenantIdentifier &&
            (stored.identifier === tenantIdentifier ||
              String(stored.id) === String(tenantIdentifier))) ||
          (!tenantIdentifier && stored); // if nothing to compare, keep previous
        if (match) tenantObj = stored;
      }
    }

    // ✅ ensure the legacy string contains only the identifier, never an object
    setTenant(tenantIdentifier || null);
    setTenantInfo(tenantObj || null);

    setUser(usr || null);
    setRoles(Array.isArray(rls) ? rls : []);
    setPermissions(Array.isArray(perms) ? perms : []);

    // LWI-856 — login (and any re-auth path that calls setSession) is
    // when we get the authoritative user identity. Any prior
    // impersonation marker is cleared above; clear it on RUM too.
    rumSetUser(usr);
    rumSetImpersonating(null);
  }

  function applySession(session) {
    setSession(session);
  }

  /** ---------------- AUTH FLOWS ---------------- */

  // Step 1: email/password (may return requires_tenant / requires_2fa / requires_2fa_setup)
  async function login(email, password, tenantIdentifier) {
    try {
      // LWI-1006: capture browser IANA timezone so scheduled reports fire at user's local clock
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { data } = await api.post("/auth/login", {
        email,
        password,
        ...(timezone ? { timezone } : {}),
        ...(tenantIdentifier ? { tenant_identifier: tenantIdentifier } : {}),
      });

      if (data.requires_2fa_setup) {
        return {
          requires2FASetup: true,
          setupId: data.setup_id,
          tenants: data.tenants || [],
          preselectTenant: data.preselect_tenant || null,
        };
      }

      if (data.requires_2fa) {
        return {
          requires2FA: true,
          challengeId: data.challenge_id,
          availableMethods: data.available_methods || ["totp"],
          tenants: data.tenants || [],
          preselectTenant: data.preselect_tenant || null,
        };
      }

      if (data.requires_tenant) {
        return { requiresTenant: true, tenants: data.tenants };
      }

      setSession({
        token: data.token,
        tenant: data.tenant, // 👈 full object
        user: data.user,
        roles: data.roles || [],
        permissions: data.permissions || [],
      });
      return { ok: true };
    } catch (err) {
      if (err.response && err.response.status === 429) {
        // Lockout/throttle error
        const msg =
          err.response.data?.message ||
          "Account temporarily locked due to failed login attempts.";
        return {
          lockedOut: true,
          message: msg,
          reason: err.response.data?.reason,
        };
      }
      if (err.response && err.response.data?.message) {
        return { error: err.response.data.message };
      }
      return { error: "Login failed. Please try again." };
    }
  }

  // Step 2 (already enrolled): verify the 6-digit code OR recovery code
  async function verifyTwoFactor({
    challengeId,
    code,
    recoveryCode,
    method,
    tenantIdentifier,
    rememberDevice,
  }) {
    const { data } = await api.post("/auth/2fa/verify", {
      challenge_id: challengeId,
      method,
      code,
      recovery_code: recoveryCode,
      tenant_identifier: tenantIdentifier,
      remember_device: !!rememberDevice,
    });
    setSession({
      token: data.token,
      tenant: data.tenant, // 👈 full object
      user: data.user,
      roles: data.roles || [],
      permissions: data.permissions || [],
    });
    return { ok: true };
  }

  /** ---- DURING-LOGIN 2FA ENROLLMENT (forced MFA) ---- */

  async function beginTwoFactorSetupDuringLogin(setupId) {
    const { data } = await api.post("/auth/2fa/setup-begin", {
      setup_id: setupId,
    });
    return data; // { otpauth_url, secret }
  }

  async function confirmTwoFactorSetupDuringLogin({
    setupId,
    code,
    tenantIdentifier,
  }) {
    const { data } = await api.post("/auth/2fa/setup-confirm", {
      setup_id: setupId,
      code,
      tenant_identifier: tenantIdentifier,
    });
    setSession({
      token: data.token,
      tenant: data.tenant, // 👈 full object
      user: data.user,
      roles: data.roles || [],
      permissions: data.permissions || [],
    });
    return { ok: true, recoveryCodes: data.recovery_codes || [] };
  }

  /** ---- IN-APP (authenticated) 2FA management ---- */

  async function beginTwoFactorSetup() {
    const { data } = await api.post("/auth/2fa/setup");
    return data;
  }

  async function confirmTwoFactorSetup(code) {
    const { data } = await api.post("/auth/2fa/confirm", { code });
    return data;
  }

  async function disableTwoFactor(password) {
    const { data } = await api.post("/auth/2fa/disable", { password });
    return data;
  }

  /** ---- EMAIL MFA — in-app management ---- */
  async function beginEmailTwoFactorSetup() {
    const { data } = await api.post("/auth/2fa/email-setup");
    return data;
  }
  async function confirmEmailTwoFactorSetup(code) {
    const { data } = await api.post("/auth/2fa/email-confirm", { code });
    return data;
  }
  async function disableEmailTwoFactor(password) {
    const { data } = await api.post("/auth/2fa/email-disable", { password });
    return data;
  }

  /** ---- EMAIL MFA — during-login enrol ---- */
  async function beginEmailTwoFactorSetupDuringLogin(setupId) {
    const { data } = await api.post("/auth/2fa/email-setup-begin", {
      setup_id: setupId,
    });
    return data;
  }
  async function confirmEmailTwoFactorSetupDuringLogin({
    setupId,
    code,
    tenantIdentifier,
  }) {
    const { data } = await api.post("/auth/2fa/email-setup-confirm", {
      setup_id: setupId,
      code,
      tenant_identifier: tenantIdentifier,
    });
    setSession({
      token: data.token,
      tenant: data.tenant,
      user: data.user,
      roles: data.roles || [],
      permissions: data.permissions || [],
    });
    return { ok: true };
  }

  /** ---- EMAIL MFA — during-login verify ---- */
  async function sendEmailLoginCode(challengeId) {
    const { data } = await api.post("/auth/2fa/send-email-code", {
      challenge_id: challengeId,
    });
    return data;
  }

  async function logout() {
    // LWI-582: clicking Logout while impersonating is "log out completely",
    // not "exit emulation". Exit the impersonation first so the bearer
    // restored for the /auth/logout call is admin's original token (which
    // backend will revoke). Otherwise we'd revoke only the impersonation
    // token and leave admin's session live.
    if (modeRef.current === "impersonation") {
      try {
        await exitImpersonation({ skipNavigate: true });
      } catch {
        // best effort; fall through to admin-side logout regardless
      }
    }

    try {
      await api.post("/auth/logout");
    } catch {}
    // LWI-592 — explicit logout clears the post-login deep-link stash so
    // that signing back in doesn't bounce the user to wherever they were
    // when they hit Sign Out. AuthGuard will re-render once state clears
    // and re-stash the *current* location (e.g. /knowledge-base/13), so
    // the caller of logout() is also responsible for clearing again
    // *after* navigating to /login (see NavbarUserDropdown.handleSignOut).
    clearRedirectAfterLogin();
    setToken(null);
    setTenant(null);
    setTenantInfo(null);
    setUser(null);
    setRoles([]);
    setPermissions([]);
    // LWI-856 — strip user identity from the RUM session.
    rumClearUser();
    rumSetImpersonating(null);
  }

  /**
   * LWI-582: enter an impersonation session. Caller passes the response from
   * POST /tenants/{tenant}/users/{id}/impersonate. We swap user/roles/
   * permissions/token to the target's, set mode = "impersonation", and stash
   * the impersonation marker. tenant + tenantInfo stay as admin's (the
   * impersonation runs within the admin's currently-active tenant).
   *
   * All updates happen in a single React 18 batched continuation so the
   * persist effects fire once with both `mode = "impersonation"` and the
   * new user/roles/permissions/token in place — they write to sessionStorage
   * uniformly.
   */
  function applyImpersonationSession(response) {
    if (!response || !response.token) return;
    setMode("impersonation");
    setImpersonation(response.impersonation || null);
    setToken(response.token);
    setUser(response.user || null);
    setRoles(Array.isArray(response.roles) ? response.roles : []);
    setPermissions(
      Array.isArray(response.permissions) ? response.permissions : []
    );
    // LWI-856 — RUM user stays as the admin (already set on login /
    // hydrate). Record who they're emulating as `usr.impersonating`.
    rumSetImpersonating(response.user);
  }

  /**
   * LWI-582: exit an impersonation session. Best-effort POST to revoke the
   * server-side token and close the session row, then clear sessionStorage
   * and restore admin's baseline from localStorage in a single batched
   * continuation. Safe to call repeatedly (idempotent on backend).
   */
  async function exitImpersonation(opts = {}) {
    const { skipNavigate = false } = opts;
    try {
      await api.post("/impersonation/exit");
    } catch {
      // server may have already revoked; we still clean up locally
    }

    const baseline = readStored();
    clearImpersonationStorage();

    setMode("admin");
    setImpersonation(null);
    setToken(baseline.token || null);
    setTenant(baseline.tenant || null);
    setTenantInfo(baseline.tenantInfo || null);
    setUser(baseline.user || null);
    setRoles(Array.isArray(baseline.roles) ? baseline.roles : []);
    setPermissions(
      Array.isArray(baseline.permissions) ? baseline.permissions : []
    );
    // LWI-856 — re-assert admin's identity in RUM defensively and drop
    // the impersonating marker.
    rumSetUser(baseline.user);
    rumSetImpersonating(null);

    if (!skipNavigate) {
      // Caller-supplied navigation is preferred (the component triggering
      // exit knows where to go); the snackbar/banner can use this default.
      // We don't force a navigate here so the caller can decide.
    }
  }

  async function switchTenant(tenantIdentifier) {
    const { data } = await api.post("/auth/switch-tenant", {
      tenant_identifier: tenantIdentifier,
    });

    // Eagerly sync the new token to localStorage and axios defaults *before* calling
    // setSession(). React state effects are async, so any request fired immediately
    // after navigation (e.g. on the destination page's mount) would otherwise still
    // read the old token from localStorage via the request interceptor and receive 401.
    if (data.token) {
      localStorage.setItem(STORAGE_KEYS.token, data.token);
      api.defaults.headers.common.Authorization = `Bearer ${data.token}`;
    }
    if (data.tenant?.identifier) {
      localStorage.setItem(STORAGE_KEYS.tenant, data.tenant.identifier);
    }

    setSession({
      token: data.token,
      tenant: data.tenant,
      user: data.user,
      roles: data.roles || [],
      permissions: data.permissions || [],
    });
    return { ok: true };
  }

  /** ---- AUTHZ HELPERS (UI gating) ---- */
  const hasRole = (role) => roles?.includes(role);
  const hasAnyRole = (someRoles) =>
    (someRoles || []).some((r) => roles?.includes(r));
  const hasPerm = (perm) => permissions?.includes(perm);
  const hasAnyPerm = (somePerms) =>
    (somePerms || []).some((p) => permissions?.includes(p));
  const hasAllPerms = (allPerms) =>
    (allPerms || []).every((p) => permissions?.includes(p));

  const value = useMemo(
    () => ({
      // raw
      token,
      tenant, // identifier string (back-compat)
      tenantInfo, // full object
      tenantName: tenantInfo?.name || "",
      user,
      roles,
      permissions,
      loading,
      isAuthenticated: !!token,

      // LWI-582 impersonation surface
      mode, // "admin" | "impersonation"
      impersonation, // { session_id, admin: {...}, target: {...}, expires_at } | null
      applyImpersonationSession,
      exitImpersonation,

      // expose for SSO callback to set session immediately
      setSession,

      // login
      login,

      // already-enrolled 2FA
      verifyTwoFactor,

      // forced MFA during login
      beginTwoFactorSetupDuringLogin,
      confirmTwoFactorSetupDuringLogin,

      // in-app 2FA management
      beginTwoFactorSetup,
      confirmTwoFactorSetup,
      disableTwoFactor,

      // email MFA
      beginEmailTwoFactorSetup,
      confirmEmailTwoFactorSetup,
      disableEmailTwoFactor,
      beginEmailTwoFactorSetupDuringLogin,
      confirmEmailTwoFactorSetupDuringLogin,
      sendEmailLoginCode,

      // misc
      logout,
      switchTenant,
      setTenant, // back-compat (identifier only)
      setTenantInfo,

      applySession,

      // password reset
      resetPassword,
      completePasswordReset,

      // authz helpers
      hasRole,
      hasAnyRole,
      hasPerm,
      hasAnyPerm,
      hasAllPerms,

      // LWI-661 — stale permissions (focus/visibility/10-min refresh)
      permissionsStale,
      acknowledgeStalePermissions: () => setPermissionsStale(false),
      refreshAuth,
    }),
    [
      token,
      tenant,
      tenantInfo,
      user,
      roles,
      permissions,
      loading,
      mode,
      impersonation,
      permissionsStale,
      refreshAuth,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
